// components/company/forms/CompanyMasterForm.jsx
import { useState, useEffect } from "react";
import { Field } from "../../ui/Field";
import { CountrySelect } from "../../ui/FormControls";

export const CompanyMasterForm = ({ data, onSubmit, meta, loading = false, countries = [] }) => {
  const [formData, setFormData] = useState(data);
  const [errors, setErrors] = useState({});
  const [showSectorOther, setShowSectorOther] = useState(false);

  // Check if current sector is "Other" or a custom value on mount
  useEffect(() => {
    if (formData.sector) {
      // Accept both canonical keys (e.g. "agriculture") and legacy labels (e.g. "Agriculture").
      const isKnownSector = meta.sectors?.some((s) => s.key === formData.sector || s.label === formData.sector);

      // Show custom input if:
      // - it's not a known key/label, OR
      // - user explicitly picked the canonical "other" key (or legacy "Other" label).
      const isOther = formData.sector === "other" || formData.sector === "Other";
      setShowSectorOther(!isKnownSector || isOther);
    }
  }, [formData.sector, meta.sectors]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = "Legal name is required";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // If "Other" with custom value, use the custom value
      const submitData = { ...formData };
      if (showSectorOther && formData.sector_other?.trim()) {
        submitData.sector = formData.sector_other.trim();
      }
      await onSubmit(submitData);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleSectorChange = (value) => {
    handleChange("sector", value);
    // Our canonical key is "other" (see backend taxonomy).
    setShowSectorOther(value === "other");
    if (value !== "other") {
      handleChange("sector_other", "");
    }
  };

  return (
    <form className="card form-section" onSubmit={handleSubmit}>
      <h3>Entity</h3>
      <div className="form-grid">
        <Field label="Public (IC) name" error={errors.ic_name}>
          <input value={formData.ic_name || ""} onChange={(e) => handleChange("ic_name", e.target.value)} aria-invalid={errors.ic_name ? "true" : "false"} />
        </Field>

        <Field label="Legal name" required error={errors.name}>
          <input value={formData.name || ""} onChange={(e) => handleChange("name", e.target.value)} required aria-invalid={errors.name ? "true" : "false"} />
        </Field>

        <Field label="Legal form" error={errors.legal_form}>
          <select value={formData.legal_form || ""} onChange={(e) => handleChange("legal_form", e.target.value)}>
            <option value="">—</option>
            {meta.legalForms.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="CoC number" error={errors.registration_no}>
          <input value={formData.registration_no || ""} onChange={(e) => handleChange("registration_no", e.target.value)} />
        </Field>

        <Field label="Country of incorporation" error={errors.country_id} help="Where this entity is registered/incorporated">
          <CountrySelect
            value={formData.country_id || ""}
            onChange={(val) => handleChange("country_id", val)}
            countries={countries}
            placeholder="Select country"
            error={errors.country_id}
          />
        </Field>

        <Field label="Residency status" error={errors.residency_status}>
          <select value={formData.residency_status || ""} onChange={(e) => handleChange("residency_status", e.target.value)}>
            <option value="">—</option>
            <option value="local">Local</option>
            <option value="international">International</option>
            <option value="foreign">Foreign</option>
          </select>
        </Field>
<Field label="Managed by" error={errors.managed_by}>
  <select value={formData.managed_by || "gtrust"} onChange={(e) => handleChange("managed_by", e.target.value)}>
    <option value="gtrust">G-Trust Curaçao B.V.</option>
    <option value="prostaff">ProStaff Curaçao N.V.</option>
  </select>
</Field>


        <Field label="Sector" error={errors.sector}>
          <select value={showSectorOther ? "other" : formData.sector || ""} onChange={(e) => handleSectorChange(e.target.value)}>
            <option value="">—</option>
            {meta.sectors.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
          {showSectorOther && (
            <input
              type="text"
              placeholder="Specify sector..."
              value={formData.sector_other || formData.sector || ""}
              onChange={(e) => handleChange("sector_other", e.target.value)}
              style={{ marginTop: "8px" }}
            />
          )}
        </Field>

        <Field label="Email" error={errors.email}>
          <input type="email" value={formData.email || ""} onChange={(e) => handleChange("email", e.target.value)} />
        </Field>

        <Field label="Phone" error={errors.phone}>
          <input type="tel" value={formData.phone || ""} onChange={(e) => handleChange("phone", e.target.value)} />
        </Field>
      </div>
      <div className="form-actions">
        <button type="submit" disabled={loading}>
          {loading ? "Saving..." : "Save Company"}
        </button>
      </div>
    </form>
  );
};
